import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  viewChild,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Network} from 'vis-network';
import {rxResource, takeUntilDestroyed, toObservable, toSignal} from '@angular/core/rxjs-interop';
import {AssetBase64, AssetUrl, AssetVisjs, VisjsJson} from 'src/app/common/models/ai-reports/shared/assets';
import {catchError, forkJoin, map, of, switchMap, tap} from 'rxjs';
import {ClientAssetService} from 'src/app/services/AIReports/client-asset-service';
import {AiChatFacade} from "@components/mainmenu/reports-menu/ai-reports/ai-reports-sub-menu/chat/ai-chat.facade";
import {VisjsRenderService} from './visjs-render.service';

@Component({
  selector: 'app-visjs-graph',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './visjs-graph.component.html',
  styleUrl: './visjs-graph.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VisjsGraphComponent {
  readonly asset = input.required<AssetVisjs>();
  readonly width = input<string>('100%');
  readonly height = input<string>('500px');

  readonly visNetwork = viewChild<ElementRef<HTMLDivElement>>('visNetwork');
  private network?: Network;
  private saveDebounce: ReturnType<typeof setTimeout> | null = null;

  private readonly clientAssetService = inject(ClientAssetService);
  private readonly renderService = inject(VisjsRenderService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly facade = inject(AiChatFacade);
  private readonly threadId = this.facade.activeThreadId();
  private readonly isMainAsset = computed(() => this.asset().id === this.facade.mainAssetId());
  private readonly shouldGeneratePreview = computed(() =>
    this.isMainAsset() && this.facade.lastGeneratedAssetId() === this.asset().id
  );

  readonly dataResource = rxResource({
    params: () => ({ storagePath: this.asset().storagePath }),
    stream: ({ params }) =>
      this.clientAssetService.downloadAssetJson<VisjsJson>(params.storagePath),
  });

  // When the JSON backgroundImage is null, fall back to the first url-type sibling
  // asset in the same result set (the AI generates it separately).
  private readonly siblingBackground = toSignal(
    toObservable(this.facade.resultAssets).pipe(
      switchMap((ids) => {
        if (!this.isMainAsset()) return of(null);
        const threadId = this.facade.activeThreadId();
        const siblingIds = ids.filter(id => id !== this.asset().id);
        if (!siblingIds.length) return of(null);
        return forkJoin(
          siblingIds.map(id =>
            this.clientAssetService.getMeta(threadId, id).pipe(catchError(() => of(null)))
          )
        ).pipe(
          switchMap(assets => {
            const bgAsset = assets.find(
              (a): a is AssetUrl | AssetBase64 => a?.type === 'url' || a?.type === 'base64'
            );
            if (!bgAsset) return of(null);
            if (bgAsset.type === 'url') return of(bgAsset.url);
            // base64: stored in Supabase — download as blob URL so auth headers are sent
            return this.clientAssetService.downloadStorageAsObjectUrl(bgAsset.storagePath).pipe(
              tap(blobUrl => this.renderService.trackBlobUrl(blobUrl)),
              catchError(() => of(null))
            );
          })
        );
      })
    ),
    { initialValue: null }
  );

  readonly backgroundImage = computed(() => {
    const fromJson = this.dataResource.hasValue() ? this.dataResource.value()?.backgroundImage : undefined;
    return fromJson ?? this.siblingBackground() ?? null;
  });

  // Pre-fetch node images as same-origin blob URLs to prevent canvas taint in preview generation.
  readonly resolvedDataResource = rxResource({
    params: () => this.dataResource.value() ?? undefined,
    stream: ({ params }) => this.renderService.resolveNodeImages(params),
  });

  constructor() {
    // rebuild vis-network when resolved data changes.
    effect(() => {
      if (!this.resolvedDataResource.hasValue()) return;
      const data = this.resolvedDataResource.value()!;
      const container = this.visNetwork()?.nativeElement;
      if (!container) return;

      this.network?.destroy();

      const options = this.renderService.buildOptions(data, this.width(), this.height());
      this.network = this.renderService.createNetwork(container, data, options);

      this.network.on('dragEnd', (params) => {
        if (!params.nodes?.length) return;
        if (this.saveDebounce) clearTimeout(this.saveDebounce);
        this.saveDebounce = setTimeout(() => this.saveGraph(), 1500);
      });
    });

    // Waits for both: a completed generation (shouldGeneratePreview) 
    // and resolved data (resolvedDataResource) before attaching afterDrawing.
    effect(() => {
      if (!this.shouldGeneratePreview() || !this.resolvedDataResource.hasValue()) return;
      const container = this.visNetwork()?.nativeElement;
      if (!this.network || !container) return;

      let captureDebounce: ReturnType<typeof setTimeout> | null = null;
      const capturePreview = () => {
        if (captureDebounce) clearTimeout(captureDebounce);
        captureDebounce = setTimeout(() => {
          this.network?.off('afterDrawing', capturePreview);
          this.generatePreview(container);
        }, 300);
      };
      this.network.on('afterDrawing', capturePreview);
    });

    this.destroyRef.onDestroy(() => {
      if (this.saveDebounce) clearTimeout(this.saveDebounce);
      this.network?.destroy();
      this.renderService.revokeBlobUrls();
    });
  }

  private async generatePreview(container: HTMLDivElement): Promise<void> {
    try {
      const blob = await this.renderService.capturePreview(container, this.backgroundImage());
      if (!blob) return;

      this.clientAssetService
        .postPreview(this.threadId, blob)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          error: (err) => console.warn('Failed to post preview', err),
        });
    } catch (err) {
      console.warn('Failed to generate preview', err);
    }
  }

  private saveGraph(): void {
    const original = this.dataResource.value();
    if (!original || !this.network) return;

    const updatedData = this.renderService.mergePositions(original, this.network);
    this.clientAssetService
      .updateContent(this.threadId, this.asset().id, updatedData, 'application/json')
      .subscribe({
        error: (err) => console.error('Failed to save graph', err),
      });
  }
}
